import { Button, Input, Select, Space, theme, Typography } from 'antd';
import {
  ClearOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { CORTE_MORA_EXPORT_FORMAT, INVOICE_TYPE } from '@aid/shared';
import type { InvoiceType } from '@aid/shared';
import { ClienteSelect } from '../../../../components/ClienteSelect';
import { DateRangeFilter, type DateRangeValue } from '../../../../components/DateRangeFilter';
import type { CorteMoraExportFormat, CorteMoraFilterState } from '../types';

const { Text } = Typography;
type Translate = (key: string, options?: Record<string, unknown>) => string;

export interface CorteMoraToolbarProps {
  t: Translate;
  filters: CorteMoraFilterState;
  resultTotal: number;
  hasFilters: boolean;
  exportingFormat: CorteMoraExportFormat | null;
  onSearchChange: (value: string) => void;
  onClientChange: (value: number | undefined) => void;
  onTipoFacturaChange: (value: InvoiceType | undefined) => void;
  onDateRangeChange: (value: DateRangeValue | null) => void;
  onClear: () => void;
  onExport: (format: CorteMoraExportFormat) => void;
}

export function CorteMoraToolbar({
  t,
  filters,
  resultTotal,
  hasFilters,
  exportingFormat,
  onSearchChange,
  onClientChange,
  onTipoFacturaChange,
  onDateRangeChange,
  onClear,
  onExport,
}: CorteMoraToolbarProps) {
  const { token } = theme.useToken();
  const dateRange: DateRangeValue | null =
    filters.fechaDesde !== undefined || filters.fechaHasta !== undefined
      ? [filters.fechaDesde ?? null, filters.fechaHasta ?? null]
      : null;

  return (
    <div style={{ display: 'grid', gap: 12, marginBottom: 16 }}>
      <div
        role="group"
        aria-label={t('pages.corteMora.filterArea')}
        className="table-filters"
        style={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 8,
        }}
      >
        <Input
          prefix={<SearchOutlined aria-hidden />}
          aria-label={t('pages.corteMora.searchPlaceholder')}
          placeholder={t('pages.corteMora.searchPlaceholder')}
          value={filters.search}
          onChange={(event) => onSearchChange(event.target.value)}
          allowClear
          style={{ width: 300, maxWidth: '100%' }}
        />
        <div style={{ width: 220, maxWidth: '100%' }}>
          <ClienteSelect
            value={filters.idCliente}
            onChange={onClientChange}
            aria-label={t('pages.corteMora.clientFilterPlaceholder')}
            placeholder={t('pages.corteMora.clientFilterPlaceholder')}
          />
        </div>
        <Select<InvoiceType>
          aria-label={t('pages.corteMora.typeFilter')}
          placeholder={t('pages.corteMora.typeFilter')}
          allowClear
          style={{ width: 180 }}
          value={filters.tipoFactura}
          onChange={(value) => onTipoFacturaChange(value as InvoiceType | undefined)}
          options={[
            {
              label: t('pages.corteMora.typePeriodic'),
              value: INVOICE_TYPE.PERIODICA,
            },
            {
              label: t('pages.corteMora.typeExtraordinary'),
              value: INVOICE_TYPE.EXTRAORDINARIA,
            },
          ]}
        />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            maxWidth: '100%',
          }}
        >
          <Text type="secondary" style={{ whiteSpace: 'nowrap', color: token.colorTextSecondary }}>
            {t('pages.corteMora.issueDate')}
          </Text>
          <DateRangeFilter
            value={dateRange}
            onChange={onDateRangeChange}
            style={{ maxWidth: '100%' }}
          />
        </div>
        <Button
          type="link"
          icon={<ClearOutlined aria-hidden />}
          aria-label={t('common.clear')}
          onClick={onClear}
          disabled={!hasFilters}
        >
          {t('common.clear')}
        </Button>
      </div>
      <div
        role="group"
        aria-label={t('pages.corteMora.exportArea')}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12,
          paddingTop: 12,
          borderTop: `1px solid ${token.colorBorderSecondary}`,
        }}
      >
        <Text type="secondary" style={{ color: token.colorTextSecondary }}>
          {t('pages.corteMora.resultCount', { count: resultTotal })}
        </Text>
        <Space wrap size={[8, 8]}>
          <Button
            icon={<FilePdfOutlined aria-hidden />}
            loading={exportingFormat === CORTE_MORA_EXPORT_FORMAT.PDF}
            disabled={exportingFormat !== null}
            aria-label={t('pages.corteMora.exportPdf')}
            onClick={() => onExport(CORTE_MORA_EXPORT_FORMAT.PDF)}
          >
            {t('pages.corteMora.exportPdf')}
          </Button>
          <Button
            icon={<FileExcelOutlined aria-hidden />}
            loading={exportingFormat === CORTE_MORA_EXPORT_FORMAT.XLSX}
            disabled={exportingFormat !== null}
            aria-label={t('pages.corteMora.exportExcel')}
            onClick={() => onExport(CORTE_MORA_EXPORT_FORMAT.XLSX)}
          >
            {t('pages.corteMora.exportExcel')}
          </Button>
        </Space>
      </div>
    </div>
  );
}
